import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { getComicById, comics, categories } from '@/mocks/comics';
import NotFound from '@/pages/NotFound';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(Number(id));
  const [showAllChapters, setShowAllChapters] = useState(false);

  if (!comic) return <NotFound />;

  const category = categories.find(c => c.slug === comic.category);
  const relatedComics = comics.filter(c => c.category === comic.category && c.id !== comic.id).slice(0, 5);
  const chaptersToShow = showAllChapters ? comic.chapters_list : comic.chapters_list.slice(0, 10);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 免费在线漫画 - 韩漫漫画网站`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: comic.chapters_list[comic.chapters_list.length - 1]?.updatedAt,
    dateModified: comic.updatedAt,
    image: comic.cover,
    publisher: {
      '@type': 'Organization',
      name: '韩漫漫画网站',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `${comic.title}是免费的吗？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `是的，${comic.title}在韩漫漫画网站提供完全免费的在线阅读体验。作为正版授权的韩国漫画平台，我们确保每一位读者都能享受高质量的漫画内容。`,
        },
      },
      {
        '@type': 'Question',
        name: `${comic.title}更新频率如何？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${comic.title}保持稳定的更新节奏，通常在每周更新1-2话。当前已更新至第${comic.chapters}话，状态为${comic.status}。`,
        },
      },
      {
        '@type': 'Question',
        name: `如何在韩漫漫画网站阅读${comic.title}？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `您可以免费在韩漫漫画网站在线阅读${comic.title}。只需进入漫画详情页，选择您想阅读的章节即可开始阅读，无需注册或付费。`,
        },
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="px-4 md:px-6 py-8">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={[
              { label: '首页', href: '/' },
              { label: category?.name || '分类', href: `/category/${comic.category}` },
              { label: comic.title },
            ]} />

            <article className="mt-6">
              <div className="flex flex-col lg:flex-row gap-8">
                <div className="flex-shrink-0 w-full sm:w-64 lg:w-72">
                  <div className="relative rounded-xl overflow-hidden aspect-[3/4]">
                    <img
                      src={comic.cover}
                      alt={`${comic.title} - 韩国漫画封面 - 免费在线漫画`}
                      title={`${comic.title} - ${comic.tags.join('、')}韩国漫画`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <h1 className="text-2xl md:text-3xl font-heading text-foreground-800 mb-3">{comic.title}</h1>

                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <span className="px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full">{comic.status}</span>
                    {comic.tags.map(tag => (
                      <span key={tag} className="px-3 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-full">{tag}</span>
                    ))}
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6 p-4 rounded-xl bg-background-100">
                    <div>
                      <p className="text-xs text-foreground-500 mb-1">作者</p>
                      <p className="text-sm font-semibold text-foreground-800">{comic.author}</p>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500 mb-1">评分</p>
                      <p className="text-sm font-semibold text-accent-600 flex items-center gap-1">
                        <i className="ri-star-fill text-xs"></i> {comic.rating}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500 mb-1">阅读量</p>
                      <p className="text-sm font-semibold text-foreground-800">{comic.views >= 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500 mb-1">总话数</p>
                      <p className="text-sm font-semibold text-foreground-800">{comic.chapters} 话</p>
                    </div>
                  </div>

                  <div className="mb-4">
                    <h2 className="text-base font-semibold text-foreground-800 mb-2">作品简介</h2>
                    <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                  </div>

                  <div className="flex items-center gap-3">
                    <Link
                      to={`/comic/${comic.id}`}
                      className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-full text-sm transition-all duration-200 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-play-circle-line mr-1.5"></i>开始阅读第1话
                    </Link>
                    <button
                      onClick={() => setShowAllChapters(true)}
                      className="px-6 py-2.5 bg-secondary-100 hover:bg-secondary-200 text-secondary-700 font-medium rounded-full text-sm transition-all duration-200 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-bookmark-line mr-1.5"></i>收藏
                    </button>
                  </div>
                </div>
              </div>

              <section className="mt-12">
                <h2 className="text-xl font-heading text-foreground-800 mb-6">章节列表</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                  {chaptersToShow.map(chapter => (
                    <Link
                      key={chapter.id}
                      to={`/comic/${comic.id}`}
                      className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 hover:bg-primary-50/20 transition-all duration-200 cursor-pointer group"
                    >
                      <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-500 transition-colors truncate">{chapter.title}</span>
                      <span className="text-xs text-foreground-400 flex-shrink-0 ml-2">{chapter.updatedAt}</span>
                    </Link>
                  ))}
                </div>
                {comic.chapters_list.length > 10 && !showAllChapters && (
                  <button
                    onClick={() => setShowAllChapters(true)}
                    className="mt-4 w-full py-3 bg-background-100 hover:bg-background-200 rounded-xl text-sm text-foreground-600 hover:text-primary-500 transition-all duration-200 cursor-pointer"
                  >
                    展开全部 {comic.chapters} 话 <i className="ri-arrow-down-s-line ml-1"></i>
                  </button>
                )}
              </section>

              {relatedComics.length > 0 && (
                <section className="mt-14">
                  <h2 className="text-xl font-heading text-foreground-800 mb-6">同类推荐</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-5">
                    {relatedComics.map(c => (
                      <ComicCard key={c.id} {...c} />
                    ))}
                  </div>
                </section>
              )}
            </article>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}